import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  acceptConsent,
  CONSENT_VERSION,
  patchSessionClientMeta,
  patchSessionSteps,
  type IntakeSession,
} from '@/lib/api/sessions';
import { answersToApi } from '@/lib/api/session-mapper';
import { JOURNEY_STEP } from '@/lib/journey-steps';

export const WIZARD_SESSION_ID_KEY = 'monday-gis-wizard-session-id';
export const WIZARD_ANSWERS_KEY = 'monday-gis-wizard-answers';
export const WIZARD_STEP_KEY = 'monday-gis-wizard-step';
export const WIZARD_CONSENT_KEY = 'monday-gis-wizard-consent-done';
export const WIZARD_INTAKE_SUBMITTED_KEY = 'monday-gis-intake-submitted';

export type WizardAnswers = {
  venture_type?: string;
  required_area_sqm?: number;
  timeline?: string;
  migrash?: string;
  /** Cadastral refs from identify — optional, for VGM 119 */
  gush?: string;
  helka?: string;
  settlement_name?: string;
  designation?: string;
  plan_primary?: string;
  area_sqm_registered?: number;
  map_lat?: number;
  map_lng?: number;
  /** Drawn parcel polygon — persisted for Monday map feature sync. */
  parcel_geometry?: { type: string; coordinates: unknown };
  selection_mode?: 'point' | 'polygon_drawn';
  area_sqm_drawn?: number;
  contact_name?: string;
  contact_phone?: string;
  contact_phone_prefix?: string;
  contact_email?: string;
};

type WizardSessionContextValue = {
  sessionId: string | null;
  currentStep: number;
  answers: WizardAnswers;
  setAnswers: (updater: WizardAnswers | ((prev: WizardAnswers) => WizardAnswers)) => void;
  setCurrentStep: (step: number) => void;
  saveStep: (
    step: number,
    answers: WizardAnswers,
    status?: string,
    apiStep?: number,
  ) => Promise<IntakeSession>;
  saveConsent: () => Promise<IntakeSession>;
  syncLocale: (locale: string) => Promise<void>;
  loading: boolean;
  error: string | null;
  clearError: () => void;
  consentComplete: boolean;
  setConsentComplete: (value: boolean) => void;
};

const WizardSessionContext = createContext<WizardSessionContextValue | null>(null);

function readSessionId(): string | null {
  try {
    return sessionStorage.getItem(WIZARD_SESSION_ID_KEY);
  } catch {
    return null;
  }
}

function readStoredStep(): number {
  try {
    const raw = sessionStorage.getItem(WIZARD_STEP_KEY);
    const n = raw != null ? Number.parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

function readStoredAnswers(): WizardAnswers {
  try {
    const raw = sessionStorage.getItem(WIZARD_ANSWERS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as WizardAnswers;
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function persistSession(sessionId: string, step: number, answers: WizardAnswers): void {
  try {
    sessionStorage.setItem(WIZARD_SESSION_ID_KEY, sessionId);
    sessionStorage.setItem(WIZARD_STEP_KEY, String(step));
    sessionStorage.setItem(WIZARD_ANSWERS_KEY, JSON.stringify(answers));
  } catch {
    /* ignore */
  }
}

export function seedWizardSession(sessionId: string, step = 0, answers: WizardAnswers = {}): void {
  persistSession(sessionId, step, answers);
}

export function isWizardConsentDone(): boolean {
  try {
    return sessionStorage.getItem(WIZARD_CONSENT_KEY) === '1';
  } catch {
    return false;
  }
}

export function markWizardConsentDone(): void {
  try {
    sessionStorage.setItem(WIZARD_CONSENT_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function clearWizardConsentDone(): void {
  try {
    sessionStorage.removeItem(WIZARD_CONSENT_KEY);
  } catch {
    /* ignore */
  }
}

export function isIntakeSubmitted(): boolean {
  try {
    return sessionStorage.getItem(WIZARD_INTAKE_SUBMITTED_KEY) === '1';
  } catch {
    return false;
  }
}

export function markIntakeSubmitted(): void {
  try {
    sessionStorage.setItem(WIZARD_INTAKE_SUBMITTED_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function clearIntakeSubmitted(): void {
  try {
    sessionStorage.removeItem(WIZARD_INTAKE_SUBMITTED_KEY);
  } catch {
    /* ignore */
  }
}

/** Reset local journey after intake is submitted — user starts fresh from landing. */
export function clearWizardSession(): void {
  try {
    sessionStorage.removeItem(WIZARD_SESSION_ID_KEY);
    sessionStorage.removeItem(WIZARD_STEP_KEY);
    sessionStorage.removeItem(WIZARD_ANSWERS_KEY);
  } catch {
    /* ignore */
  }
  clearWizardConsentDone();
  clearIntakeSubmitted();
}

export function WizardSessionProvider({
  children,
  requireConsent = false,
}: {
  children: ReactNode;
  requireConsent?: boolean;
}) {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState<string | null>(() => readSessionId());
  const [currentStep, setCurrentStepState] = useState(readStoredStep);
  const [answers, setAnswersState] = useState<WizardAnswers>(readStoredAnswers);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consentComplete, setConsentComplete] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const id = readSessionId();
    if (!id) {
      navigate('/', { replace: true });
      return;
    }
    if (requireConsent && !isWizardConsentDone()) {
      navigate('/questionnaire', { replace: true });
      return;
    }
    setSessionId(id);
    setCurrentStepState(readStoredStep());
    setAnswersState(readStoredAnswers());
    setConsentComplete(isWizardConsentDone());
    setReady(true);

    void patchSessionSteps(id, {
      current_step: readStoredStep(),
      answers: answersToApi(readStoredAnswers()),
    })
      .then((session) => {
        if (session.consent_at) {
          markWizardConsentDone();
          setConsentComplete(true);
        }
      })
      .catch(() => {
        /* best-effort sync */
      });
  }, [navigate, requireConsent]);

  const setAnswers = useCallback(
    (updater: WizardAnswers | ((prev: WizardAnswers) => WizardAnswers)) => {
      setAnswersState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (sessionId) {
          persistSession(sessionId, currentStep, next);
        }
        return next;
      });
    },
    [sessionId, currentStep],
  );

  const setCurrentStep = useCallback(
    (step: number) => {
      setCurrentStepState(step);
      if (sessionId) {
        persistSession(sessionId, step, answers);
      }
    },
    [sessionId, answers],
  );

  const saveStep = useCallback(
    async (
      step: number,
      nextAnswers: WizardAnswers,
      status?: string,
      apiStep?: number,
    ) => {
      if (!sessionId) {
        throw new Error('No session');
      }
      setLoading(true);
      setError(null);
      try {
        const session = await patchSessionSteps(sessionId, {
          current_step: apiStep ?? step,
          answers: answersToApi(nextAnswers),
          status,
        });
        const merged = (session.answers ?? nextAnswers) as WizardAnswers;
        setAnswersState(merged);
        setCurrentStepState(step);
        persistSession(sessionId, step, merged);
        return session;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Save failed';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [sessionId],
  );

  const saveConsent = useCallback(async () => {
    if (!sessionId) {
      throw new Error('No session');
    }
    setLoading(true);
    setError(null);
    try {
      const session = await acceptConsent(sessionId, {
        consent_version: CONSENT_VERSION,
      });
      await patchSessionSteps(sessionId, {
        current_step: JOURNEY_STEP.CONSENT,
        answers: answersToApi(answers),
        status: 'questionnaire_done',
      });
      markWizardConsentDone();
      setConsentComplete(true);
      return session;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Consent failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [sessionId, answers]);

  const syncLocale = useCallback(
    async (locale: string) => {
      if (!sessionId) return;
      try {
        await patchSessionClientMeta(sessionId, { client_meta: { locale } });
      } catch {
        /* locale sync is best-effort */
      }
    },
    [sessionId],
  );

  const value = useMemo(
    () => ({
      sessionId,
      currentStep,
      answers,
      setAnswers,
      setCurrentStep,
      saveStep,
      saveConsent,
      syncLocale,
      loading,
      error,
      clearError: () => setError(null),
      consentComplete,
      setConsentComplete,
    }),
    [
      sessionId,
      currentStep,
      answers,
      saveStep,
      saveConsent,
      syncLocale,
      loading,
      error,
      consentComplete,
    ],
  );

  if (!ready || !sessionId) {
    return null;
  }

  return (
    <WizardSessionContext.Provider value={value}>{children}</WizardSessionContext.Provider>
  );
}

export function useWizardSession(): WizardSessionContextValue {
  const ctx = useContext(WizardSessionContext);
  if (!ctx) {
    throw new Error('useWizardSession must be used within WizardSessionProvider');
  }
  return ctx;
}
